import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SkeletonFormSection } from '../components/common';
import {
  ProfileSection,
  TimetableSection,
  NotificationSettings,
  CalendarSettings,
  SettingsRow,
  timetableValue,
  countCalendarsNeedingAttention,
} from '../components/settings';
import { useTheme } from '../contexts/ThemeContext';
import { useSettingsPage } from '../hooks/useSettingsPage';
import { useFamilyPage } from '../hooks/useFamily';
import { useFamilyStore } from '../stores';
import { HomeLocationSection } from '../components/family/HomeLocationSection';
import { BottomNav } from '../components/navigation';
import './Settings.css';
import './Family.css';

// Fixed codes from the OAuth callback's calendar-connect branches.
const CONNECT_ERROR_COPY: Record<string, string> = {
  google_account_mismatch:
    "That Google account doesn't match the one linked to your VizDay " +
    'account. Approve access with the same Google account.',
  google_denied: 'Google access was declined, so the calendar was not connected.',
  session_expired: 'That connection attempt expired. Try again.',
};

/** How the "Sign-in" row names the account's provider; absent → no value. */
const AUTH_PROVIDER_LABEL: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  email: 'Email and password',
};

type SectionKey = 'identity' | 'home' | 'sign-in' | 'calendars' | 'notifications' | 'sunset' | 'about';

/** Which section an arrival opens: a hash from another screen, or the
 *  calendar-consent return (so its outcome is on screen, not folded away). */
function initialSection(hash: string, search: string): SectionKey | null {
  if (hash === '#family') return 'home';
  if (hash === '#account') return 'sign-in';
  if (hash === '#add-calendar') return 'calendars';
  const params = new URLSearchParams(search);
  if (params.get('google_calendar') === 'connected' || params.get('error')) return 'calendars';
  return null;
}

const Settings: React.FC = () => {
  const s = useSettingsPage();
  const location = useLocation();
  const familyStore = useFamilyStore();
  const familyPage = useFamilyPage();
  const { tagSunsetStops, setTagSunsetStops } = useTheme();

  // A contents page (Almanac S6): each row opens its section in place.
  const [open, setOpen] = useState<SectionKey | null>(
    () => initialSection(location.hash, window.location.search),
  );
  const toggle = (key: SectionKey) => setOpen((prev) => (prev === key ? null : key));

  // Scroll to #family anchor when navigating from YouV2. The sheet's "Add"
  // arrives as #add-calendar: Calendars opens with its add form showing, and
  // again on each such arrival (keyed on location.key, so a repeat press from
  // the sheet reopens a form that was cancelled under the same hash).
  const setShowAddCalendar = s.setShowAddCalendar;
  useEffect(() => {
    if (location.hash === '#family') {
      setOpen('home');
      setTimeout(() => {
        document.getElementById('family-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else if (location.hash === '#account') {
      setOpen('sign-in');
    } else if (location.hash === '#add-calendar') {
      setOpen('calendars');
      setShowAddCalendar(true);
    }
  }, [location.hash, location.key, setShowAddCalendar]);

  // Returning from the Google Calendar consent screen — kick off the first
  // sync on success; on failure say what happened. The server sends a fixed
  // code (never Google's own text), and an unrecognized code still gets the
  // generic line rather than silence.
  const handleSyncGoogle = s.handleSyncGoogle;
  const showError = s.showError;
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('google_calendar') === 'connected';
    const errorCode = params.get('error');
    if (!connected && !errorCode) return;
    params.delete('google_calendar');
    params.delete('error');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    if (connected) {
      handleSyncGoogle();
    } else {
      showError(
        CONNECT_ERROR_COPY[errorCode as string] ??
          "Google Calendar couldn't be connected. Try again."
      );
    }
  }, [handleSyncGoogle, showError]);

  const fullName = s.user
    ? `${s.user.first_name || ''} ${s.user.last_name || ''}`.trim()
    : '';
  const family = familyStore.family;
  const homeLabel = family?.home_city
    ? `${family.home_city}${family.home_state ? `, ${family.home_state}` : ''}`
    : undefined;
  const subscribed = s.subscribedCalendars ?? [];
  // The same count the gear's mark reads (D14), every provider with health.
  const calendarNeedCount = countCalendarsNeedingAttention(s.googleCalendars, subscribed);
  const calendarsValue = calendarNeedCount > 0
    ? `${calendarNeedCount} ${calendarNeedCount === 1 ? 'needs' : 'need'} you`
    : [
        s.googleCalendars.length > 0 && 'Google',
        s.microsoftCalendars.length > 0 && 'Microsoft',
        subscribed.length > 0 && `${subscribed.length} ICS`,
      ].filter(Boolean).join(', ') || 'Not connected';
  const profileProps = s.user ? {
    user: s.user,
    showChangePassword: s.showChangePassword,
    setShowChangePassword: s.setShowChangePassword,
    currentPassword: s.currentPassword,
    setCurrentPassword: s.setCurrentPassword,
    newPassword: s.newPassword,
    setNewPassword: s.setNewPassword,
    confirmPassword: s.confirmPassword,
    setConfirmPassword: s.setConfirmPassword,
    isChangingPassword: s.isChangingPassword,
    onChangePassword: s.handleChangePassword,
  } : null;

  if (s.isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-container">
          <div className="settings-loading-skeleton">
            <SkeletonFormSection />
            <SkeletonFormSection />
            <SkeletonFormSection />
          </div>
        </div>
      </div>
    );
  }

  if (s.settingsError && !s.preferences) {
    return (
      <div className="settings-page">
        <div className="settings-scroll-container">
          <header className="settings-header">
            <div className="settings-header-top">
              <h1>Settings</h1>
            </div>
          </header>
          <div className="settings-container">
            <div className="settings-error">
              <span className="error-icon">&#9888;&#65039;</span>
              <h3>Couldn't load settings</h3>
              <p>Check your connection and try again.</p>
              <button className="retry-btn" onClick={() => s.fetchSettings()}>Retry</button>
            </div>
          </div>
        </div>
        <BottomNav activeItem="today" />
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-scroll-container">
        {/* Page Header */}
        <header className="settings-header">
          <div className="settings-header-top">
            <h1>Settings</h1>
          </div>
        </header>

        <div className="settings-container">

        {s.message && (
          <div className={`settings-message ${s.message.type}`}>
            {s.message.text}
          </div>
        )}

        <div className="alm-settings">
          {/* ─── You ─── */}
          {s.user && (
            <>
              <h2 className="alm-set-h">You</h2>
              <div className="alm-set-group">
                <SettingsRow
                  label="Name and email"
                  value={fullName || s.user.email}
                  expanded={open === 'identity'}
                  onToggle={() => toggle('identity')}
                  controls="settings-open-identity"
                  testId="settings-row-identity"
                />
                {open === 'identity' && (
                  <div id="settings-open-identity" className="alm-open-body">
                    {profileProps && <ProfileSection {...profileProps} part="identity" />}
                    {/* Deleting the account is one level down, away from Save. */}
                    <div className="alm-account-foot">
                      <Link to="/settings/delete-account" className="alm-link alm-danger">
                        Delete account
                      </Link>
                    </div>
                  </div>
                )}

                {familyStore.family && (
                  <>
                    <SettingsRow
                      label="Home"
                      value={homeLabel}
                      expanded={open === 'home'}
                      onToggle={() => toggle('home')}
                      controls="family-section"
                      testId="settings-row-home"
                    />
                    {open === 'home' && (
                      /* Home Location — powers weather and travel-time context */
                      <div id="family-section" className="family-page settings-family-scope alm-open-body">
                        <section className="settings-section">
                          <HomeLocationSection family={familyStore.family} onUpdate={familyPage.handleFamilyUpdate} />
                        </section>
                      </div>
                    )}
                  </>
                )}

                <SettingsRow
                  label="Sign-in"
                  value={s.user.auth_provider ? AUTH_PROVIDER_LABEL[s.user.auth_provider] : undefined}
                  expanded={open === 'sign-in'}
                  onToggle={() => toggle('sign-in')}
                  controls="settings-open-sign-in"
                  testId="settings-row-sign-in"
                />
                {open === 'sign-in' && (
                  <div id="settings-open-sign-in" className="alm-open-body">
                    {profileProps && <ProfileSection {...profileProps} part="sign-in" />}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ─── Your days ─── */}
          <h2 className="alm-set-h">Your days</h2>
          <div className="alm-set-group">
            <SettingsRow
              label="Calendars"
              value={calendarsValue}
              need={calendarNeedCount > 0}
              expanded={open === 'calendars'}
              onToggle={() => toggle('calendars')}
              controls="settings-open-calendars"
              testId="settings-row-calendars"
            />
            {open === 'calendars' && (
              <div id="settings-open-calendars" className="alm-open-body">
                <CalendarSettings
                  googleCalendars={s.googleCalendars}
                  microsoftCalendars={s.microsoftCalendars}
                  microsoftStatus={s.microsoftStatus}
                  subscribedCalendars={s.subscribedCalendars}
                  onRetryCalendar={s.handleRetryCalendar}
                  familyMembers={s.familyMembers}
                  isSyncing={s.isSyncing}
                  isSyncingMicrosoft={s.isSyncingMicrosoft}
                  showAddCalendar={s.showAddCalendar}
                  setShowAddCalendar={s.setShowAddCalendar}
                  newCalendarName={s.newCalendarName}
                  setNewCalendarName={s.setNewCalendarName}
                  newCalendarUrl={s.newCalendarUrl}
                  setNewCalendarUrl={s.setNewCalendarUrl}
                  newCalendarMember={s.newCalendarMember}
                  setNewCalendarMember={s.setNewCalendarMember}
                  isAdding={s.isAdding}
                  editingCalendarId={s.editingCalendarId}
                  editingUrl={s.editingUrl}
                  setEditingUrl={s.setEditingUrl}
                  onSyncGoogle={s.handleSyncGoogle}
                  onToggleGoogleCalendar={s.handleToggleGoogleCalendar}
                  onConnectGoogle={s.handleConnectGoogle}
                  onSyncMicrosoft={s.handleSyncMicrosoft}
                  onToggleMicrosoftCalendar={s.handleToggleMicrosoftCalendar}
                  onConnectMicrosoft={s.handleConnectMicrosoft}
                  onSyncSubscribed={s.handleSyncSubscribed}
                  onAddCalendar={s.handleAddCalendar}
                  onAssignMember={s.handleAssignMember}
                  onRemoveCalendar={s.handleRemoveCalendar}
                  onEditUrl={s.handleEditUrl}
                  onSaveUrl={s.handleSaveUrl}
                  onCancelEditUrl={s.handleCancelEditUrl}
                />
              </div>
            )}

            <SettingsRow
              label="Notifications"
              expanded={open === 'notifications'}
              onToggle={() => toggle('notifications')}
              controls="settings-open-notifications"
              testId="settings-row-notifications"
            />
            {open === 'notifications' && (
              <div id="settings-open-notifications" className="alm-open-body">
                <NotificationSettings
                  preferences={s.preferences}
                  onToggle={s.handleToggle}
                  onTimeChange={s.handleTimeChange}
                  onMinutesChange={s.handleMinutesChange}
                />
              </div>
            )}

            <SettingsRow
              label="Timetable"
              value={timetableValue(tagSunsetStops)}
              expanded={open === 'sunset'}
              onToggle={() => toggle('sunset')}
              controls="settings-open-sunset"
              testId="settings-row-sunset"
            />
            {open === 'sunset' && (
              <div id="settings-open-sunset" className="alm-open-body">
                <TimetableSection
                  tagSunsetStops={tagSunsetStops}
                  onTagSunsetChange={setTagSunsetStops}
                />
              </div>
            )}
          </div>

          {/* ─── VizDay ─── */}
          <h2 className="alm-set-h">VizDay</h2>
          <div className="alm-set-group">
            <SettingsRow
              label="About"
              value="VizDay v2.0"
              expanded={open === 'about'}
              onToggle={() => toggle('about')}
              controls="settings-open-about"
              testId="settings-row-about"
            />
            {open === 'about' && (
              <div id="settings-open-about" className="alm-open-body">
                <p className="section-hint">VizDay v2.0 · VizDNA LLC</p>
                <p className="section-hint">
                  <Link to="/terms" className="alm-link">Terms of Service</Link>
                  {' · '}
                  <Link to="/privacy" className="alm-link">Privacy Policy</Link>
                </p>
              </div>
            )}
          </div>

          {/* ─── Sign out: a plain link at the bottom ─── */}
          <div className="alm-signout">
            <button type="button" onClick={s.handleSignOut} className="alm-link">
              Sign out
            </button>
          </div>
        </div>
        </div>
      </div>

      <BottomNav activeItem="today" />
    </div>
  );
};

export default Settings;
